/** @jsxImportSource @emotion/react */

import React, { FC } from 'react'
import { Field, Form as FinalForm } from 'react-final-form'
import Button from '@mui/material/Button'
import arrayMutators from 'final-form-arrays'
import Typography from '@mui/material/Typography'
import { FormApi, SubmissionErrors } from 'final-form'
import { useNavigate } from 'react-router-dom'
import createDecorator from 'final-form-focus'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FormHelperText from '@mui/material/FormHelperText'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { PriceInput } from 'components/Input/PriceInput'
import { DropzoneField, ImageField } from 'components/DropzoneField'
import { SelectField } from 'components/Select'
import { CheckboxField } from 'components/Checkbox'
import { AdditionalTestTypeEnum, ProductStatus, ProductTypeEnum } from '__generatedTypes__/globalTypes'
import { findAllPracticeTest_findAllPracticeTest_items as PracticeTest } from '__generatedTypes__/findAllPracticeTest'
import { CourseName } from 'theme/utils'
import { setFieldData } from 'utils/setFieldData'
import * as classes from './styles'

export type Status = { id: ProductStatus; title: string }

export const STATUSES: Array<Status> = [
  {
    id: ProductStatus.Active,
    title: 'Active',
  },
  {
    id: ProductStatus.Inactive,
    title: 'Inactive',
  },
]

const DROPZONE_OPTIONS = {
  accept: {
    'image/*': [],
  },
}

export type FormValues = {
  type?: AdditionalTestTypeEnum
  name?: string
  description?: string
  subject?: string
  practiceTestIds?: string[]
  price?: number
  status?: Status
  isPopular?: boolean
  imageLight?: ImageField[]
  imageDark?: ImageField[]
}

type Props = {
  initialValues: FormValues
  onSubmit: (
    values: FormValues,
    form: FormApi<FormValues>,
    callback?: (errors?: SubmissionErrors) => void
  ) => SubmissionErrors | Promise<SubmissionErrors> | void
  premiumPTs: PracticeTest[]
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const Form: FC<Props> = ({ initialValues, onSubmit, premiumPTs }) => {
  const navigate = useNavigate()
  const isAdditionalFLTTests = initialValues.type === AdditionalTestTypeEnum.AdditionalFLTests
  const isHiddenSubject = initialValues.subject === CourseName.Hidden

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        Edit Additional Tests Product
      </Typography>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators, setFieldData }}
        decorators={[focusOnErrors]}
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, form, values }) => (
          <form css={classes.form} onSubmit={handleSubmit}>
            <InputField name="name" validate={validateRequired} label="Name" placeholder="Enter Name" fullWidth />
            {!isAdditionalFLTTests && !isHiddenSubject && (
              <InputField name="subject" label="Subject" placeholder="Enter Subject" fullWidth disabled />
            )}
            <InputField
              name="description"
              validate={validateRequired}
              label="Description"
              placeholder="Enter Description"
              fullWidth
              multiline
            />

            {!isAdditionalFLTTests && (
              <div>
                <div css={classes.labelWrapper}>Practice Tests (Tests that are marked as Premium Membership Max)</div>
                <Field name="practiceTestIds" validate={validateRequired}>
                  {({ input, meta }) => (
                    <div>
                      <div css={classes.optionsWrapper}>
                        {premiumPTs.map(({ id, title }) => {
                          const practiceTestIds = values.practiceTestIds ?? []
                          const isSelected = practiceTestIds.includes(id as ProductTypeEnum)
                          return (
                            <FormControlLabel
                              key={id}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onClick={() =>
                                    form.change(
                                      'practiceTestIds',
                                      (isSelected
                                        ? practiceTestIds.filter((val) => val !== id)
                                        : [...practiceTestIds, id]) as ProductTypeEnum[]
                                    )
                                  }
                                />
                              }
                              label={title}
                            />
                          )
                        })}
                      </div>
                      {!!meta.touched && !input.value?.length && (
                        <FormHelperText error>Please select at least one Practice Test</FormHelperText>
                      )}
                    </div>
                  )}
                </Field>
              </div>
            )}
            <PriceInput
              name="price"
              validate={validateRequired}
              label="Price"
              placeholder="Enter Price"
              prefix="$"
              fullWidth
            />
            <SelectField
              name="status"
              validate={validateRequired}
              items={STATUSES}
              label="Status"
              placeholder="Choose Status"
              displaySelectedItemsFirst
              fullWidth
            />
            <CheckboxField name="isPopular" label="Is this product most popular?" />
            <div css={classes.columnsWrapper}>
              <div>
                <div css={classes.labelWrapper}>Light Version</div>
                <DropzoneField
                  name="imageLight"
                  validate={validateRequired}
                  dropzoneOptions={DROPZONE_OPTIONS}
                  options={{
                    maxFiles: 1,
                    sliceFromEnd: true,
                  }}
                />
              </div>
              <div>
                <div css={classes.labelWrapper}>Dark Version</div>
                <DropzoneField
                  name="imageDark"
                  validate={validateRequired}
                  dropzoneOptions={DROPZONE_OPTIONS}
                  options={{
                    maxFiles: 1,
                    sliceFromEnd: true,
                  }}
                />
              </div>
            </div>

            <div css={classes.buttonsWrapper}>
              <Button
                variant="outlined"
                color="red"
                size="large"
                fullWidth
                disabled={submitting}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button variant="contained" type="submit" color="blue" size="large" fullWidth disabled={submitting}>
                Update Product
              </Button>
            </div>
          </form>
        )}
      />
    </section>
  )
}
